import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  Stack,
  Paper,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Divider,
  Dialog,
  IconButton,
  Tooltip
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import MiniDrawer from "../../components/MiniDrawer.js";
import { useTheme } from '@mui/material/styles';
import useBillingsData from "./BillingsData.js";
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventIcon from '@mui/icons-material/Event';
import ReplayIcon from '@mui/icons-material/Replay';
import BillingDocumentDialog from "./BillingDocumentDialog.js";
import OrganizationSettlements, { BankPayoutCell, SettlementSummary } from "../../components/OrgPayouts/OrganizationSettlements";
import { orgPayoutsEnabled } from "../../components/OrgPayouts/api";

import { customerPaymentStatus } from './billingPresentation';

const BillingsContent = () => {
  const {
    loading,
    timeRange,
    setTimeRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    statusFilter,
    setStatusFilter,
    searchText,
    setSearchText,
    paged,
    page,
    setPage,
    totalPages,
    totals,
    formatMoney,
    getBillingStatus,
    getDoctorProfile,
    getPatientDiagnosis,
    isAdmin,
    getTherapistTag
  } = useBillingsData();

  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState('Invoice');
  const [selected, setSelected] = useState(null);
  const [doctorProfile, setDoctorProfile] = useState(null);
  const [diagnosisList, setDiagnosisList] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState(false);
  const [sourceAttempt, setSourceAttempt] = useState(0);

  useEffect(() => {
    if (!selected || !open) return undefined;
    let active = true;
    setDoctorProfile(null);
    setDiagnosisList([]);
    setSourceLoading(true);
    setSourceError(false);
    Promise.all([getDoctorProfile(selected.doctor_id), getPatientDiagnosis(selected.patient_id)])
      .then(([profile, diagnoses]) => {
        if (!profile) throw new Error('Issuer unavailable');
        if (active) { setDoctorProfile(profile); setDiagnosisList(diagnoses || []); }
      }).catch(() => { if (active) setSourceError(true); })
      .finally(() => { if (active) setSourceLoading(false); });
    return () => { active = false; };
  }, [selected, open, getDoctorProfile, getPatientDiagnosis, sourceAttempt]);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-HK', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const formatShortDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-HK');
  };

  const handleOpen = (row, type) => {
    setSourceLoading(true);
    setSelected(row);
    setDocType(type);
    setOpen(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const durationMinutes = useCallback((row) => {
    if (!row?.appointment_starttime || !row?.appointment_endtime) return '--';
    const start = new Date(row.appointment_starttime);
    const end = new Date(row.appointment_endtime);
    const mins = Math.max(0, Math.round((end - start) / 60000));
    return `${mins} mins`;
  }, []);

  return (
    <Container disableGutters maxWidth={false}>
      <Grid container sx={{ overflow: 'auto', minHeight: '100vh', minWidth: '100vw' }}>
        <Grid size={{ xs: 1, md: 0 }}>
          <MiniDrawer />
        </Grid>
        <Grid size="grow">
          <Grid container spacing={5} sx={{ backgroundColor: '#F8F9FA', display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
            <Grid size={12}>
              <Box sx={{ px: { xs: '20px', sm: '10vw' }, pt: '24px', pb: '40px' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Billing & Earnings</Typography>
                  </Box>
                </Stack>

                {/* KPI Cards */}
                <Grid container spacing={3} sx={{ mb: 3, width: '100%' }}>
                  {[
                    {
                      title: 'Total Earnings',
                      value: formatMoney(totals.total),
                      sub: `${totals.totalCount} sessions`,
                      icon: <AttachMoneyIcon sx={{ color: '#0E7C7B', fontSize: 22 }} />,
                      iconBg: '#DFF7F5',
                      subColor: '#64748B',
                    },
                    {
                      title: orgPayoutsEnabled ? 'Transferred to Stripe' : 'Paid',
                      value: formatMoney(totals.paid),
                      sub: `${totals.paidCount} sessions`,
                      icon: <TrendingUpIcon sx={{ color: '#16A34A', fontSize: 22 }} />,
                      iconBg: '#E7F9EF',
                      subColor: '#16A34A',
                    },
                    {
                      title: orgPayoutsEnabled ? 'Awaiting transfer' : 'Pending',
                      value: formatMoney(totals.pending),
                      sub: `${totals.pendingCount} sessions`,
                      icon: <EventIcon sx={{ color: '#CA8A04', fontSize: 20 }} />,
                      iconBg: '#FFF4CC',
                      subColor: '#F97316',
                    },
                    {
                      title: 'Refunded',
                      value: formatMoney(totals.refunded),
                      sub: `${totals.refundedCount} sessions`,
                      icon: <ReplayIcon sx={{ color: '#DC2626', fontSize: 20 }} />,
                      iconBg: '#FEE2E2',
                      subColor: '#DC2626',
                    },
                  ].map((card, idx) => (
                    <Grid key={idx} size={{ xs: 6, md: 3 }}>
                      <Paper
                        sx={{
                          p: { xs: 2, sm: 3 },
                          borderRadius: 3,
                          border: '1px solid #E5E7EB',
                          boxShadow: 'none',
                          height: '100%',
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {card.title}
                          </Typography>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: card.iconBg,
                            }}
                          >
                            {card.icon}
                          </Box>
                        </Stack>

                        <Typography variant="h4" sx={{ fontWeight: 800, mt: 2 }}>
                          {card.value}
                        </Typography>

                        <Typography variant="caption" sx={{ color: card.subColor }}>
                          {card.sub}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <SettlementSummary />
                {/* Filters */}
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    mb: 3,
                    border: '1px solid #E5E7EB',
                    boxShadow: 'none',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                <Stack direction="row" spacing={3} alignItems="flex-end" sx={{ flexWrap: 'wrap' }}>
                    <Stack spacing={0.5}>
                        <Typography variant="caption" sx={{ color: 'transparent', fontWeight: 600 }}>
                        Filters
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                            sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#F1F5F9',
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M3 5h18l-7 8v5l-4 2v-7L3 5z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600 }}>
                            Filters:
                        </Typography>
                        </Stack>
                    </Stack>

                    <Stack spacing={0.5}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                        Period
                      </Typography>
                      <FormControl size="small">
                        <Select
                          value={timeRange}
                          onChange={(e) => setTimeRange(e.target.value)}
                          sx={{
                            borderRadius: 2.5,
                            minWidth: 240,
                            backgroundColor: '#F8FAFC',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                          }}
                        >
                          <MenuItem value="week">This Week</MenuItem>
                          <MenuItem value="month">This Month</MenuItem>
                          <MenuItem value="quarter">This Quarter</MenuItem>
                          <MenuItem value="year">This Year</MenuItem>
                          <MenuItem value="custom">Custom Range</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>

                    <Stack spacing={0.5}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                        {orgPayoutsEnabled ? 'Transfer status' : 'Status'}
                      </Typography>
                      <FormControl size="small">
                        <Select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          sx={{
                            borderRadius: 2.5,
                            minWidth: 160,
                            backgroundColor: '#F8FAFC',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                          }}
                        >
                          <MenuItem value="All">All</MenuItem>
                          <MenuItem value="Paid">{orgPayoutsEnabled ? 'Transferred to Stripe' : 'Paid'}</MenuItem>
                          <MenuItem value="Pending">{orgPayoutsEnabled ? 'Awaiting transfer' : 'Pending'}</MenuItem>
                          <MenuItem value="Refunded">Refunded</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>

                    <Stack spacing={0.5} sx={{ flexGrow: 1, minWidth: 220 }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                        Search
                      </Typography>
                      <TextField
                        size="small"
                        placeholder={isAdmin ? "Search by case ID, client or therapist..." : "Search by case ID or client..."}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        sx={{
                          borderRadius: 2.5,
                          backgroundColor: '#F8FAFC',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                        }}
                      />
                    </Stack>
                    {timeRange === 'custom' && (
                        <Stack spacing={0.5}>
                        <Typography variant="caption" sx={{ color: 'transparent', fontWeight: 600 }}>
                            Custom
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                            size="small"
                            type="date"
                            value={customFrom}
                            onChange={(e) => setCustomFrom(e.target.value)}
                            sx={{
                                minWidth: 140,
                                backgroundColor: '#F8FAFC',
                                '& .MuiOutlinedInput-root': { height: 40 },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                            }}
                            />
                            <TextField
                            size="small"
                            type="date"
                            value={customTo}
                            onChange={(e) => setCustomTo(e.target.value)}
                            sx={{
                                minWidth: 140,
                                backgroundColor: '#F8FAFC',
                                '& .MuiOutlinedInput-root': { height: 40 },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                            }}
                            />
                            <Button
                            size="small"
                            variant="outlined"
                            onClick={() => { setCustomFrom(''); setCustomTo(''); }}
                            sx={{
                                height: 40,
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: '#E5E7EB',
                            }}
                            >
                            Reset
                            </Button>
                        </Stack>
                        </Stack>
                    )}
                  </Stack>
                </Paper>

                {/* Table */}
                <Paper sx={{ p: 2, borderRadius: 3 }}>
                  <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
                    {paged.map(row => <Box key={row.id} sx={{ p: 2, border: '1px solid #E2E8ED', borderRadius: 2 }}>
                      <Stack direction="row" justifyContent="space-between" gap={1}><Typography fontWeight={600}>{row.patient_name || 'Client'}</Typography><Typography fontWeight={600}>{formatMoney((row.payment_total_amount_cents || 0) - (row.payment_platform_fee_cents || 0))}</Typography></Stack>
                      <Typography variant="caption" color="text.secondary">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'} · {getTherapistTag(row).label}</Typography>
                      <Stack spacing={1} sx={{ mt: 2 }}>
                        <Typography variant="body2">Customer payment: {customerPaymentStatus(row)}</Typography>{row.appointment_status === 'cancelled' && <Chip label="Appointment cancelled" size="small" sx={{ alignSelf: 'flex-start', bgcolor: '#F1F5F9', color: '#475569' }} />}
                        {orgPayoutsEnabled ? <><Typography variant="body2" fontWeight={600}>Bank payout</Typography><BankPayoutCell appointmentId={row.id} clientName={row.patient_name} date={row.created_at} appointment={row} /></> : <Typography variant="body2">Transfer status: {getBillingStatus(row)}</Typography>}
                        <Stack direction="row" gap={1}><Button size="small" onClick={() => handleOpen(row, 'Invoice')}>Invoice</Button><Button size="small" onClick={() => handleOpen(row, 'Receipt')}>Receipt</Button></Stack>
                      </Stack>
                    </Box>)}
                  </Stack>
                  <TableContainer sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 850 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Case ID</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Client</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Therapist</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{orgPayoutsEnabled ? 'Customer payment' : 'Status'}</TableCell>
                        {orgPayoutsEnabled && <TableCell>Bank payout</TableCell>}
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Session Type</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Duration</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Listed Amount</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{isAdmin ? 'Net Earning' : 'Your Net Earning'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paged.map((row) => {
                        const status = orgPayoutsEnabled ? customerPaymentStatus(row) : getBillingStatus(row);
                        const therapist = getTherapistTag(row);
                        return (
                          <TableRow key={row.id}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Tooltip title={row.id || ''} placement="top">
                                <Typography variant="body2" sx={{ color: '#29C0BB', fontWeight: 'bold', fontFamily: 'monospace', cursor: 'default' }}>
                                  {row.id ? `${row.id.substring(0, 8)}…` : '—'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{row.patient_name || '—'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Chip
                                label={therapist.label}
                                size="small"
                                sx={therapist.isMe
                                  ? { backgroundColor: '#DFF7F5', color: '#0E7C7B', fontWeight: 700 }
                                  : { backgroundColor: '#F1F5F9', color: '#334155', fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell>{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</TableCell>
                            <TableCell>
                              {orgPayoutsEnabled ? <Chip label={status} size="small" sx={{ bgcolor: status === 'Paid' ? '#DCFCE7' : '#F1F5F9', color: '#253346', fontWeight: 600 }} /> : <>
                              {status === 'Paid' && <Chip label="Paid" size="small" sx={{ backgroundColor: '#DCFCE7', color: '#166534', fontWeight: 600 }} />}
                              {status === 'Pending' && <Chip label="Pending" size="small" sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 600 }} />}
                              {status === 'Refunded' && <Chip label="Refunded" size="small" sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 600 }} />}
                              {status === '—' && <Typography variant="caption">—</Typography>}
                              </>}
                              {row.appointment_status === 'cancelled' && <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>Appointment cancelled</Typography>}
                            </TableCell>
                            {orgPayoutsEnabled && <TableCell><BankPayoutCell appointmentId={row.id} clientName={row.patient_name} date={row.created_at} appointment={row} /></TableCell>}
                            <TableCell>
                              {row.category === 'First consultation'
                                ? 'First Session'
                                : row.category === 'Existing patient'
                                ? 'Regular Session'
                                : row.category || '—'}
                            </TableCell>
                            <TableCell>{durationMinutes(row)}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{formatMoney(row.payment_total_amount_cents)}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{formatMoney((row.payment_total_amount_cents || 0) - (row.payment_platform_fee_cents || 0))}</TableCell>
                            <TableCell>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleOpen(row, 'Invoice')}
                                    startIcon={<DescriptionIcon />}
                                    sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    color: '#000',
                                    borderColor: '#E0E0E0',
                                    '&:hover': {
                                        backgroundColor: '#F5F5F5',
                                        borderColor: '#BDBDBD',
                                    },
                                    }}
                                >
                                    Invoice
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleOpen(row, 'Receipt')}
                                    startIcon={<ReceiptIcon />}
                                    sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    color: '#000',
                                    borderColor: '#E0E0E0',
                                    '&:hover': {
                                        backgroundColor: '#F5F5F5',
                                        borderColor: '#BDBDBD',
                                    },
                                    }}
                                >
                                    Receipt
                                </Button>
                            </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </TableContainer>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption">
                      Showing {paged.length} sessions
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                        Previous
                      </Button>
                      <Button size="small" variant="outlined" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                        Next
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>

                {/* Invoice/Receipt dialog */}
                {open && selected && <BillingDocumentDialog
                  key={`${selected.id}:${docType}`}
                  sourceLoading={sourceLoading}
                  sourceError={sourceError}
                  onRetrySource={() => setSourceAttempt(value => value + 1)}
                  open={open}
                  onClose={() => setOpen(false)}
                  docType={docType}
                  selected={selected}
                  doctorProfile={doctorProfile}
                  diagnosisList={diagnosisList}
                  formatMoney={formatMoney}
                  getBillingStatus={getBillingStatus}
                  formatDate={formatDate}
                  formatShortDate={formatShortDate}
                  durationMinutes={durationMinutes}
                />}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

const BillingsView = () => <OrganizationSettlements><BillingsContent /></OrganizationSettlements>;
export default BillingsView;
